import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, TWITTER_HANDLE } from '../config/seo';

/**
 * SEO component: title, description, Open Graph, Twitter Card, canonical, noindex, JSON-LD.
 * Use on every public page. For admin/login pass noindex={true}.
 *
 * @param {string} title - Page title (site name is appended)
 * @param {string} description - Meta description (keep 150–160 chars for best results)
 * @param {string} [image] - OG/Twitter image URL (absolute or relative; relative is resolved against SITE_URL)
 * @param {string} [canonical] - Canonical URL (default: current path on SITE_URL)
 * @param {boolean} [noindex] - If true, adds robots noindex,nofollow
 * @param {string} [ogType] - og:type, default "website"
 * @param {object|object[]} [jsonLd] - JSON-LD structured data (object or array of objects)
 * @param {string} [titleTemplate] - If set, overrides default " | Digital Fatehabad" (set to "" for no suffix)
 */
const SEO = ({
  title,
  description,
  image,
  canonical,
  noindex = false,
  ogType = 'website',
  jsonLd,
  titleTemplate = ` | ${SITE_NAME}`,
}) => {
  const fullTitle = title ? `${title}${titleTemplate}` : SITE_NAME;
  const img = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`)
    : DEFAULT_OG_IMAGE;
  const canonicalUrl = canonical
    ? (canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`)
    : (typeof window !== 'undefined' ? `${SITE_URL}${window.location.pathname || ''}` : SITE_URL);

  const ldJson = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || ''} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || ''} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || ''} />
      <meta name="twitter:image" content={img} />
      {TWITTER_HANDLE && <meta name="twitter:site" content={TWITTER_HANDLE} />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* JSON-LD */}
      {ldJson.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
